import sescheduleIcon from '@brands/Dashboard/Sidenav/Icons/EventFilled.svg';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { UserProfile } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { setDependentId, setIsFromSelectPatient } from '@brands/store/slices/pageStateSlice';
import { setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { checkExternalFormAnswers } from '@brands/Utils/checkExternalForms';
import { displayErrorDetails } from '@brands/Utils/displayError';
import React, { useEffect, useState } from 'react';

import { CaseType, ICase } from '../../../../services/cases/types/ICase';
import { rescheduleCaseAvailability } from '../../../../Utils/getFriendlyName';
import styles from '../appointments.module.scss';

interface Props {
  appointment: ICase;
  rescheduleAvailability: boolean;
}

const RescheduleButton = ({ appointment, rescheduleAvailability }: Props): JSX.Element => {
  const [rescheduleCaseAvailabilityFlag, setRescheduleCaseAvailabilityFlag] = useState(rescheduleAvailability);
  const roleNavigate = useRoleNavigate();
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);

  const reschedule = async (): Promise<void> => {
    if (!rescheduleCaseAvailability(appointment)) return;

    // Reschedule never routes through SelectPatient/SelectCaseType, the only
    // two screens that normally gate on profile/clinical-data completeness,
    // so a linked case sitting in "upcoming" could be rescheduled straight
    // through without the patient ever having filled in required info.
    // Deliberately not passing openDependentProfile/selectedDependentId here:
    // Profile.tsx's own effect treats that combination as "came from case
    // creation" and force-navigates to /select-patient once the dependent
    // edit closes, which would hijack this save just like the Redux
    // isFromSelectPatient coupling does in EditProfile/EditDependent - reset
    // that instead of opting into it, and land on the plain profile page.
    const [profileComplete, medicalHistoryComplete, medicalConditionsComplete] = await checkExternalFormAnswers(
      userInfo,
      appointment.patient as UserProfile,
    );
    // Medical history is only mandatory for Phone/Video Call consultations
    // (matches ClinicalDataStepEight/Nine's own CLINICAL_CASES scoping) -
    // Oral Health Coaching never collects it, so don't block on it here.
    const requiresMedicalHistory =
      appointment.type === CaseType.video_call_scheduled || appointment.type === CaseType.phone_call_scheduled;
    const missingMedicalHistory = requiresMedicalHistory && (!medicalHistoryComplete || !medicalConditionsComplete);

    if (!profileComplete || missingMedicalHistory) {
      dispatch(setIsFromSelectPatient(false));
      dispatch(setDependentId(null));
      if (missingMedicalHistory) {
        // /medical-history (unlike /my-profile) reads which patient it's
        // editing from this "who is this for" selector, the same one
        // SelectPatient/SelectCaseType set for the new-case wizard - without
        // it, a dependent's incomplete record would show the guardian's own.
        dispatch(
          setSelectedPatient({ value: appointment.patient.id.toString(), label: appointment.patient.full_name }),
        );
      }
      // /my-profile has no medical-history editing UI in every brand - the
      // dedicated /medical-history route is the one place that's guaranteed
      // to exist everywhere (also used by the new-case ClinicalData wizard).
      roleNavigate(!profileComplete ? '/my-profile' : '/medical-history');
      return;
    }

    const selectionRoute =
      appointment.type !== CaseType.oral_health_coaching ? '/select-provider' : '/select-care-advisor';

    const searchParams = new URLSearchParams({
      reschedule: 'true',
      caseId: appointment.id.toString(),
      caseType: appointment.type,
    });

    roleNavigate(`${selectionRoute}?${searchParams.toString()}`, {
      state: {
        RescheduleCase: true,
        caseId: appointment.id,
        providerId: appointment?.provider?.id || 0,
        careAdvisorId: appointment?.care_advisor_id || 0,
        caseType: appointment?.type,
        patient_current_state: appointment.patient_current_state,
        RescheduleCaseSelectedCarrierId: appointment?.insurance?.insurance_membership?.insurance_carrier?.id || null,
      },
    });
  };

  const handleReschedule = async (): Promise<void> => {
    try {
      await reschedule();
    } catch (error: unknown) {
      displayErrorDetails(error, 'Unable to check your information. Please check your connection and try again.');
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (
      (appointment.type === CaseType.video_call_scheduled ||
        appointment.type === CaseType.phone_call_scheduled ||
        appointment.type === CaseType.oral_health_coaching) &&
      appointment.video_conference_date &&
      rescheduleCaseAvailabilityFlag
    ) {
      intervalId = setInterval(() => {
        const dueDate = rescheduleCaseAvailability(appointment);
        setRescheduleCaseAvailabilityFlag(!!dueDate);
      }, 10000);
    }
    return () => clearInterval(intervalId);
  }, [appointment]);

  return (
    <span>
      {rescheduleCaseAvailabilityFlag && (
        <button type="button" className={`fs-unmask ${styles.cancelBtn}`} onClick={handleReschedule}>
          <img src={sescheduleIcon} />
          Reschedule
        </button>
      )}
    </span>
  );
};

export default RescheduleButton;
